/**
 * MyShipmentsPage — list of the current user's shipments with a status
 * filter strip. Mirrors Flutter `my_shipments_page.dart`.
 */
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Search, Loader, Package } from 'lucide-react';
import { useMyShipments } from '@/hooks/useShipments';
import { AppLayout } from '@/components/layout/AppLayout';
import { ShipmentCard } from '@/components/shipments/ShipmentCard';
import type { Shipment } from '@/types/shipment';

type FilterKey = 'all' | 'active' | 'pending' | 'in_transit' | 'delivered' | 'cancelled';

const FILTERS: { key: FilterKey; labelKey: string; fallback: string; match: (s: Shipment) => boolean }[] = [
  { key: 'all',        labelKey: 'all',           fallback: 'الكل',        match: () => true },
  { key: 'pending',    labelKey: 'pending',       fallback: 'بانتظار سائق', match: (s) => s.status === 'pending' || s.status === 'awaiting_approval' || s.status === 'awaiting_sender_approval' },
  { key: 'active',     labelKey: 'active',        fallback: 'نشطة',        match: (s) => ['accepted', 'picked_up', 'in_transit', 'at_center', 'out_for_delivery'].includes(s.status) },
  { key: 'in_transit', labelKey: 'in_transit',    fallback: 'في الطريق',    match: (s) => s.status === 'in_transit' || s.status === 'out_for_delivery' },
  { key: 'delivered',  labelKey: 'delivered',     fallback: 'مُسلَّمة',     match: (s) => s.status === 'delivered' },
  { key: 'cancelled',  labelKey: 'cancelled',     fallback: 'ملغاة',       match: (s) => s.status === 'cancelled' || s.status === 'returned' },
];

export default function MyShipmentsPage() {
  const { t } = useTranslation();
  const { data: shipments, isLoading, isError, refetch } = useMyShipments('sender');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    if (!shipments) return [];
    const q = query.trim().toLowerCase();
    const cfg = FILTERS.find((f) => f.key === filter)!;
    return shipments.filter((s) => {
      if (!cfg.match(s)) return false;
      if (!q) return true;
      return (
        s.tracking_number?.toLowerCase().includes(q) ||
        s.receiver_name?.toLowerCase().includes(q) ||
        s.receiver_phone?.includes(q) ||
        s.receiver_city?.toLowerCase().includes(q) ||
        s.pickup_city?.toLowerCase().includes(q)
      );
    });
  }, [shipments, filter, query]);

  return (
    <AppLayout>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-text-primary">{t('my_shipments', 'شحناتي')}</h1>
        <Link
          to="/shipments/create"
          className="inline-flex items-center gap-1.5 bg-primary text-white rounded-xl px-4 py-2 text-sm font-semibold hover:bg-primary-light"
        >
          <Plus size={16} /> {t('new_shipment', 'شحنة جديدة')}
        </Link>
      </div>

      {/* Filters */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-3">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm border transition ${
              filter === f.key
                ? 'bg-primary text-white border-primary'
                : 'bg-surface text-text-secondary border-border hover:border-primary'
            }`}
          >
            {t(`filter.${f.labelKey}`, f.fallback)}
          </button>
        ))}
      </div>

      {/* Search */}
      <label className="flex items-center gap-2 bg-surface border border-border rounded-xl px-3 py-2 mb-4">
        <Search size={16} className="text-text-tertiary" />
        <input
          type="search"
          placeholder={t('search_shipments', 'بحث برقم تتبّع، مستلِم، مدينة…')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="flex-1 outline-none bg-transparent text-sm"
        />
      </label>

      {/* List */}
      {isLoading ? (
        <div className="flex justify-center py-12 text-text-tertiary">
          <Loader size={28} className="animate-spin" />
        </div>
      ) : isError ? (
        <div className="bg-error/10 text-error rounded-xl p-4 text-sm flex items-center justify-between">
          <span>{t('load_failed', 'فشل التحميل')}</span>
          <button onClick={() => refetch()} className="underline">
            {t('retry', 'إعادة المحاولة')}
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-surface border border-border rounded-xl p-10 text-center">
          <Package size={40} className="mx-auto text-text-tertiary mb-3" />
          <p className="text-text-secondary">
            {query
              ? t('no_results', 'لا توجد نتائج مطابقة')
              : t('no_shipments_in_filter', 'لا شحنات في هذا التصفية')}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((s) => (
            <ShipmentCard key={s.id} shipment={s} />
          ))}
        </div>
      )}
    </AppLayout>
  );
}
